import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import {
  ClipboardList, Phone, Cpu, Package, Zap, HeartHandshake,
  ArrowRight, CheckCircle2, ShieldCheck, Lock, Wifi,
} from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const phases = [
  {
    number: '01',
    icon: <ClipboardList className="w-7 h-7" />,
    name: 'The Discovery Form',
    tagline: 'We start by learning how you actually run — your tools, your team, your friction points.',
    timeline: '20–30 min',
    href: '/process/discover',
  },
  {
    number: '02',
    icon: <Phone className="w-7 h-7" />,
    name: 'The Strategy Call',
    tagline: 'A recorded conversation that becomes the brain of your agent.',
    timeline: '1 hour',
    href: '/process/strategy-call',
  },
  {
    number: '03',
    icon: <Cpu className="w-7 h-7" />,
    name: 'Building Your Agent',
    tagline: 'We do the work. Trained, integrated, hardened, and tested before it ships.',
    timeline: '5–7 business days',
    href: '/process/build',
  },
  {
    number: '04',
    icon: <Package className="w-7 h-7" />,
    name: 'Secure Delivery',
    tagline: 'Your machine arrives at your door, configured and ready to plug in.',
    timeline: '3–5 business days',
    href: '/process/delivery',
  },
  {
    number: '05',
    icon: <Zap className="w-7 h-7" />,
    name: 'The Activation Call',
    tagline: 'You meet your agent for the first time. We go live together.',
    timeline: '3 hours',
    href: '/process/activation',
  },
  {
    number: '06',
    icon: <HeartHandshake className="w-7 h-7" />,
    name: 'Ongoing Support',
    tagline: 'Your agent keeps learning. You always have backup. Winning Wednesday, every week.',
    timeline: 'Ongoing',
    href: '/process/support',
  },
];

const integrations = [
  'AccuLynx', 'JobNimbus', 'CompanyCam', 'ProLine',
  'RoofLink', 'HailTrace', 'Dialpad', 'CallRail',
];

const ProcessPage = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const ctx = gsap.context(() => {
      // Hero entrance
      gsap.fromTo(
        '.hero-content > *',
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.9, stagger: 0.12, ease: 'power3.out' }
      );

      // Phase cards
      gsap.utils.toArray('.phase-card').forEach((card, i) => {
        gsap.fromTo(
          card,
          { y: 30, opacity: 0 },
          {
            y: 0,
            opacity: 1,
            duration: 0.7,
            ease: 'power2.out',
            delay: (i % 2) * 0.08,
            scrollTrigger: { trigger: card, start: 'top 88%' },
          }
        );
      });

      // Integration tags
      gsap.utils.toArray('.integration-tag').forEach((tag, i) => {
        gsap.fromTo(
          tag,
          { scale: 0.85, opacity: 0 },
          {
            scale: 1,
            opacity: 1,
            duration: 0.45,
            ease: 'back.out(1.4)',
            delay: i * 0.05,
            scrollTrigger: { trigger: tag, start: 'top 92%' },
          }
        );
      });

      // Outcome section
      gsap.fromTo(
        '.outcome-section',
        { y: 40, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 0.9,
          ease: 'power3.out',
          scrollTrigger: { trigger: '.outcome-section', start: 'top 80%' },
        }
      );
    }, containerRef);
    return () => ctx.revert();
  }, []);

  // Schema markup for SEO
  const schemaData = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How RoofClaw Works — The Build Process',
    description:
      'Every RoofClaw is custom-built for your roofing business. Here\'s exactly what we deliver, phase by phase — from your first call to a live AI agent working for you.',
    step: phases.map((phase, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: phase.name,
      text: phase.tagline,
      url: `https://roofclaw.com${phase.href}`,
    })),
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'What does a RoofClaw agent actually do?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'A RoofClaw agent handles the administrative and communication tasks that slow your business down — CRM updates, follow-ups, scheduling, data entry, and more. It connects to the tools you already use and works inside them, 24 hours a day.',
        },
      },
      {
        '@type': 'Question',
        name: 'Will RoofClaw work with my software?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'We have pre-built connections for AccuLynx, JobNimbus, RoofLink, ProLine, CompanyCam, HailTrace, Dialpad, and CallRail. If your tool isn\'t on that list, we research and build the connection during your build phase.',
        },
      },
      {
        '@type': 'Question',
        name: 'Why does a roofing business need this?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Roofers lose deals to slow follow-up, miss collections from lack of persistence, and burn hours on data entry. A RoofClaw agent handles all of it — without taking a day off, forgetting a lead, or calling in sick.',
        },
      },
    ],
  };

  return (
    <div
      className="bg-[#0A0A14] text-[#F0EFF4] min-h-screen selection:bg-[#7B61FF] selection:text-[#F0EFF4]"
      ref={containerRef}
    >
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />

      {/* Hero */}
      <div className="relative min-h-[85vh] flex items-end pb-24">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('/images/generated/process-hero-hub.png')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A14] via-[#0A0A14]/60 to-[#0A0A14]/20" />
        <div className="relative max-w-5xl mx-auto px-6 w-full hero-content">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#7B61FF]/10 border border-[#7B61FF]/20 text-xs font-semibold tracking-wider text-[#7B61FF] mb-6 font-data">
            <CheckCircle2 className="w-4 h-4" />
            HOW ROOFCLAW WORKS
          </div>
          <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 leading-tight">
            Built For Your Business.{' '}
            <span className="font-drama italic text-[#7B61FF] font-normal tracking-normal block md:inline">
              Every Time.
            </span>
          </h1>
          <p className="text-lg md:text-xl text-[#F0EFF4]/70 max-w-2xl leading-relaxed">
            Most AI is generic. RoofClaw isn't. Every agent starts with a deep understanding of how
            you run — your tools, your team, your goals. Then we build something that works exactly
            the way you do.
          </p>
        </div>
      </div>

      {/* The Journey — Phase Cards */}
      <div className="max-w-5xl mx-auto px-6 py-24">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">The Journey</h2>
          <p className="text-[#F0EFF4]/55 max-w-xl mx-auto">
            Six phases from your first conversation to a live AI agent — and the ongoing relationship after.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {phases.map((phase) => (
            <Link
              to={phase.href}
              key={phase.number}
              className="phase-card group bg-[#18181B] rounded-[2rem] p-8 border border-[#7B61FF]/15 hover:border-[#7B61FF]/50 hover:shadow-[0_0_30px_rgba(123,97,255,0.1)] transition-all duration-300"
            >
              <div className="flex items-start justify-between mb-6">
                <div className="w-14 h-14 bg-[#7B61FF]/10 rounded-2xl flex items-center justify-center text-[#7B61FF] group-hover:bg-[#7B61FF]/20 transition-colors">
                  {phase.icon}
                </div>
                <span className="font-data text-[#7B61FF]/35 text-xs font-bold tracking-widest">
                  {phase.number}
                </span>
              </div>
              <div className="mb-2">
                <span className="font-data text-[10px] font-bold tracking-widest text-[#7B61FF]/60 uppercase">
                  {phase.timeline}
                </span>
              </div>
              <h3 className="text-xl font-bold mb-3">{phase.name}</h3>
              <p className="text-[#F0EFF4]/55 text-sm leading-relaxed mb-6">{phase.tagline}</p>
              <div className="flex items-center gap-2 text-[#7B61FF] text-sm font-semibold">
                Learn more{' '}
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Integration Section */}
      <div className="border-t border-[#7B61FF]/10 py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#7B61FF]/10 border border-[#7B61FF]/20 text-xs font-semibold tracking-wider text-[#7B61FF] mb-6 font-data">
                INTEGRATIONS
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6 leading-snug">
                Your tools don't change.{' '}
                <span className="font-drama italic text-[#7B61FF] font-normal tracking-normal text-4xl md:text-5xl">
                  Your agent learns them.
                </span>
              </h2>
              <p className="text-[#F0EFF4]/60 leading-relaxed mb-4">
                We don't ask you to switch platforms. We connect your agent to what you already use and
                make it work harder.
              </p>
              <p className="text-[#F0EFF4]/60 leading-relaxed">
                We have pre-built connections for the most common roofing tools. If yours isn't on the
                list, we research and build it during your build phase.
              </p>
            </div>
            <div>
              <div className="flex flex-wrap gap-3">
                {integrations.map((tool) => (
                  <span
                    key={tool}
                    className="integration-tag px-4 py-2 bg-[#18181B] border border-[#7B61FF]/20 rounded-full text-sm font-semibold text-[#F0EFF4]/75 hover:border-[#7B61FF]/50 hover:text-[#F0EFF4] transition-all cursor-default"
                  >
                    {tool}
                  </span>
                ))}
                <span className="integration-tag px-4 py-2 bg-[#7B61FF]/10 border border-[#7B61FF]/30 rounded-full text-sm font-semibold text-[#7B61FF]">
                  + Your Tools
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Security Strip */}
      <div className="border-t border-[#7B61FF]/10 py-24">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#7B61FF]/10 border border-[#7B61FF]/20 text-xs font-semibold tracking-wider text-[#7B61FF] mb-5 font-data">
              SECURITY
            </div>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Built like a vault.{' '}
              <span className="font-drama italic text-[#7B61FF] font-normal tracking-normal text-4xl md:text-5xl">
                Runs like a teammate.
              </span>
            </h2>
            <p className="text-[#F0EFF4]/55 max-w-xl mx-auto">
              Every decision in our build process — hardware, credentials, networking — defaults to the most secure option available.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: <Lock className="w-6 h-6" />,
                title: 'Encrypted Hardware',
                desc: 'FileVault disk encryption enabled before your machine ships. If it ever gets lost or stolen, the data is unreadable. Full stop.',
              },
              {
                icon: <ShieldCheck className="w-6 h-6" />,
                title: 'One-Time Credential Transfer',
                desc: 'Your API keys are submitted via Password Pusher — a link that self-destructs after a single view. They go directly into your build environment and are never logged to this portal.',
              },
              {
                icon: <Wifi className="w-6 h-6" />,
                title: 'Zero Internet Exposure',
                desc: "Your machine has no open ports and isn't visible on the public internet. Remote access runs through a private encrypted mesh — invisible to everything outside it.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="bg-[#18181B] rounded-[2rem] p-8 border border-[#7B61FF]/15 hover:border-[#7B61FF]/40 transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[#7B61FF]/10 rounded-xl flex items-center justify-center text-[#7B61FF] mb-5">
                  {item.icon}
                </div>
                <h3 className="text-lg font-bold mb-3">{item.title}</h3>
                <p className="text-[#F0EFF4]/55 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Outcome Promise */}
      <div className="outcome-section py-28 bg-[#18181B]/40">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#7B61FF]/10 border border-[#7B61FF]/20 text-xs font-semibold tracking-wider text-[#7B61FF] mb-8 font-data">
            THE OUTCOME
          </div>
          <h2 className="text-3xl md:text-5xl font-bold mb-8 leading-tight">
            By the end of this process, you have an AI employee that knows your business, works your
            tools, and is available{' '}
            <span className="font-drama italic text-[#7B61FF] font-normal tracking-normal text-4xl md:text-6xl">
              24/7.
            </span>
          </h2>
          <p className="text-[#F0EFF4]/60 text-lg mb-12 max-w-2xl mx-auto">
            Not a chatbot. Not a demo. A real working system — yours, on your hardware, in your
            office. We don't retain access. By design.
          </p>
          <a
            href="https://buy.stripe.com/14A8wOgNKfKz3HedcT2kw07"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center px-10 py-5 rounded-full bg-[#7B61FF] text-[#F0EFF4] font-bold tracking-wide hover:-translate-y-1 hover:scale-[1.03] transition-all duration-500 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] shadow-[0_0_30px_rgba(123,97,255,0.4)] hover:shadow-[0_0_50px_rgba(123,97,255,0.6)]"
          >
            Reserve Your Build
          </a>
          <p className="mt-6 text-xs font-data text-[#F0EFF4]/40 uppercase tracking-widest">
            Build schedule fills approximately 14 days in advance.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProcessPage;
